import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface GeneralSettings {
  platformName: string;
  supportEmail: string;
  supportPhone: string;
  timezone: string;
  currency: string;
  currencySymbol: string;
  defaultLanguage: string;
  platformFeePercent: number;
  maintenanceMode: boolean;
}

export interface BusinessSettings {
  companyName: string;
  legalEntityName: string;
  gstin: string;
  panNumber: string;
  registeredAddress: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  contactEmail: string;
  contactPhone: string;
  websiteUrl: string;
  logoUrl?: string;
}

export interface PaymentGatewaySettings {
  defaultGateway: 'Razorpay' | 'Stripe' | 'Cashfree';
  razorpayEnabled: boolean;
  razorpayKeyId: string;
  razorpayKeySecret: string;
  razorpayWebhookSecret: string;
  stripeEnabled: boolean;
  stripePublishableKey: string;
  stripeSecretKey: string;
  cashfreeEnabled: boolean;
  cashfreeAppId: string;
  cashfreeSecretKey: string;
  upiId: string;
  autoInvoicing: boolean;
  taxGstRate: number;
}

export interface NotificationSettings {
  emailOnNewClient: boolean;
  emailOnSubscriptionRenew: boolean;
  emailOnPaymentSuccess: boolean;
  emailOnPaymentFailure: boolean;
  emailOnSubscriptionExpiryWarning: boolean;
  smsAlertsEnabled: boolean;
  whatsappAlertsEnabled: boolean;
  adminAlertEmails: string;
  adminAlertPhones: string;
}

export interface SecuritySettings {
  twoFactorAuthEnabled: boolean;
  sessionTimeoutMinutes: number;
  minPasswordLength: number;
  requireUppercase: boolean;
  requireSpecialChar: boolean;
  maxFailedLoginAttempts: number;
  ipWhitelistEnabled: boolean;
  whitelistedIps: string;
  auditLoggingEnabled: boolean;
}

export interface SettingsState {
  general: GeneralSettings;
  business: BusinessSettings;
  payment: PaymentGatewaySettings;
  notifications: NotificationSettings;
  security: SecuritySettings;
  loading: boolean;
  savedMessage: string | null;
  error: string | null;
}

const initialState: SettingsState = {
  general: {
    platformName: 'Electionsetu Admin Platform',
    supportEmail: 'support@electionsetu.in',
    supportPhone: '+91 80000 12345',
    timezone: 'Asia/Kolkata (IST +05:30)',
    currency: 'INR',
    currencySymbol: '₹',
    defaultLanguage: 'English',
    platformFeePercent: 0,
    maintenanceMode: false,
  },
  business: {
    companyName: 'Electionsetu Technologies Pvt. Ltd.',
    legalEntityName: 'Electionsetu Technologies Private Limited',
    gstin: '07AAACE9876K1Z5',
    panNumber: 'AAACE9876K',
    registeredAddress: 'Level 5, Innovate Tower, Cyber City',
    city: 'Gurugram',
    state: 'Haryana',
    pincode: '122002',
    country: 'India',
    contactEmail: 'contact@electionsetu.in',
    contactPhone: '+91 124 4567890',
    websiteUrl: 'https://electionsetu.in',
    logoUrl: '/logo/logo_full.png',
  },
  payment: {
    defaultGateway: 'Razorpay',
    razorpayEnabled: true,
    razorpayKeyId: 'rzp_live_891273912830',
    razorpayKeySecret: '••••••••••••••••••••••••••••••••',
    razorpayWebhookSecret: '••••••••••••••••••••••••',
    stripeEnabled: false,
    stripePublishableKey: '',
    stripeSecretKey: '',
    cashfreeEnabled: false,
    cashfreeAppId: '',
    cashfreeSecretKey: '',
    upiId: 'electionsetu@icici',
    autoInvoicing: true,
    taxGstRate: 18,
  },
  notifications: {
    emailOnNewClient: true,
    emailOnSubscriptionRenew: true,
    emailOnPaymentSuccess: true,
    emailOnPaymentFailure: true,
    emailOnSubscriptionExpiryWarning: true,
    smsAlertsEnabled: true,
    whatsappAlertsEnabled: true,
    adminAlertEmails: 'admin@electionsetu.in, alerts@electionsetu.in',
    adminAlertPhones: '+919876543210',
  },
  security: {
    twoFactorAuthEnabled: true,
    sessionTimeoutMinutes: 60,
    minPasswordLength: 8,
    requireUppercase: true,
    requireSpecialChar: true,
    maxFailedLoginAttempts: 5,
    ipWhitelistEnabled: false,
    whitelistedIps: '103.21.244.0/24, 104.16.0.0/12',
    auditLoggingEnabled: true,
  },
  loading: false,
  savedMessage: null,
  error: null,
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setSettingsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setSavedMessage: (state, action: PayloadAction<string | null>) => {
      state.savedMessage = action.payload;
    },
    updateGeneralSettingsReducer: (state, action: PayloadAction<Partial<GeneralSettings>>) => {
      state.general = { ...state.general, ...action.payload };
    },
    updateBusinessSettingsReducer: (state, action: PayloadAction<Partial<BusinessSettings>>) => {
      state.business = { ...state.business, ...action.payload };
    },
    updatePaymentSettingsReducer: (state, action: PayloadAction<Partial<PaymentGatewaySettings>>) => {
      state.payment = { ...state.payment, ...action.payload };
    },
    updateNotificationSettingsReducer: (state, action: PayloadAction<Partial<NotificationSettings>>) => {
      state.notifications = { ...state.notifications, ...action.payload };
    },
    updateSecuritySettingsReducer: (state, action: PayloadAction<Partial<SecuritySettings>>) => {
      state.security = { ...state.security, ...action.payload };
    },
  },
});

export const {
  setSettingsLoading,
  setSavedMessage,
  updateGeneralSettingsReducer,
  updateBusinessSettingsReducer,
  updatePaymentSettingsReducer,
  updateNotificationSettingsReducer,
  updateSecuritySettingsReducer,
} = settingsSlice.actions;

export default settingsSlice.reducer;
